import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PlayCircle, FileText, Globe, Package, Headphones, ExternalLink, Loader2 } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const getAuthToken = () => 
  localStorage.getItem("token") || 
  localStorage.getItem("access_token") || 
  localStorage.getItem("authToken") || 
  "";

const VideoPlayer = ({ videoUrl, title }) => {
  // Handle different video formats and sources
  const getVideoSource = (url) => {
    if (!url) return '';
    
    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtube.com') 
        ? url.split('v=')[1]?.split('&')[0]
        : url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    
    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }
    
    return url;
  };

  const videoSrc = getVideoSource(videoUrl);
  const isEmbeddedVideo =
    videoSrc.includes('youtube.com/embed/') || videoSrc.includes('player.vimeo.com/video/');

  return (
    <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-xl border border-border relative group">
      {videoSrc ? (
        isEmbeddedVideo ? (
          <iframe
            src={videoSrc}
            className="w-full h-full"
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            src={videoSrc}
            className="w-full h-full"
            controls
            controlsList="nodownload"
            onContextMenu={(event) => event.preventDefault()}
          />
        )
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center text-white">
            <PlayCircle className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p>Video not available</p>
          </div>
        </div>
      )}
    </div>
  );
};

const AudioPlayer = ({ audioUrl, title }) => {
  const getAudioSource = (url) => {
    if (!url) return '';
    
    // Handle different audio formats
    const supportedFormats = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
    const hasValidFormat = supportedFormats.some(format => 
      url.toLowerCase().includes(format)
    );
    
    return hasValidFormat ? url : '';
  };

  const audioSrc = getAudioSource(audioUrl);

  return (
    <div className="bg-card flex flex-col h-[calc(100vh-140px)] border rounded-lg overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-3">
          <Headphones className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-foreground/80 tracking-tight">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Download button removed for audio content */}
        </div>
      </div>
      <div className="flex-1 bg-muted/10 relative flex items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          {audioSrc ? (
            <audio 
              controls 
              className="w-full" 
              style={{ width: '100%' }}
              controlsList="nodownload"
              onContextMenu={(event) => event.preventDefault()}
            >
              <source src={audioSrc} type="audio/mpeg" />
              <source src={audioSrc} type="audio/wav" />
              <source src={audioSrc} type="audio/ogg" />
              <source src={audioSrc} type="audio/m4a" />
              Your browser does not support the audio element.
            </audio>
          ) : (
            <div className="text-center">
              <Headphones className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Audio not available</p>
              <p className="text-sm text-slate-400 mt-2">Unsupported audio format or missing file</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const URLViewer = ({ url, title }) => {
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="bg-card flex flex-col h-[calc(100vh-140px)] border rounded-lg overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-3">
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-foreground/80 tracking-tight">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {isValidUrl(url) && (
            <Button variant="outline" size="sm" className="h-8 text-[10px] font-bold gap-2" asChild>
              <a href={url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3 w-3" /> Open in New Tab
              </a>
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 bg-muted/10 relative">
        {isValidUrl(url) ? (
          <iframe
            src={url}
            className="w-full h-full border-none"
            title="URL Content"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Globe className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Invalid URL</p>
              <p className="text-sm text-slate-400 mt-2">The provided URL is not valid</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SCORMViewer = ({ scormUrl, title }) => {
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="bg-card flex flex-col h-[calc(100vh-140px)] border rounded-lg overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-3">
          <Package className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-foreground/80 tracking-tight">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-orange-50 text-orange-600 border-none font-bold text-[10px] px-3 py-1">
            SCORM Content
          </Badge>
        </div>
      </div>
      <div className="flex-1 bg-muted/10 relative">
        {isValidUrl(scormUrl) ? (
          <iframe
            src={scormUrl}
            className="w-full h-full border-none"
            title="SCORM Content"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">SCORM package not available</p>
              <p className="text-sm text-slate-400 mt-2">Invalid SCORM URL</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DocumentReader = ({ title, documentUrl }) => {
  const getDocumentViewer = (url) => {
    if (!url) return null;

    const urlPath = url.toLowerCase().split('?')[0].split('#')[0];
    const fileExtension = urlPath.split('.').pop();
    
    // Handle Office documents with Office Online Viewer
    if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(fileExtension)) {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      return { type: 'office', url: officeUrl };
    }
    
    // Handle PDF with direct embedding and CSS to hide toolbar
    if (fileExtension === 'pdf') {
      return { type: 'pdf', url: url };
    }
    
    // Handle images
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(fileExtension)) {
      return { type: 'image', url: url };
    }
    
    // Handle text files
    if (['txt', 'md', 'csv'].includes(fileExtension)) {
      return { type: 'text', url: url };
    }
    
    return { type: 'unsupported', url: url };
  };

  const viewerConfig = getDocumentViewer(documentUrl);

  return (
    <div className="bg-card flex flex-col h-[calc(100vh-140px)] border rounded-lg overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b flex items-center justify-between bg-muted/30">
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-foreground/80 tracking-tight">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Download and fullscreen buttons removed for document content */}
          {viewerConfig?.type !== 'pdf' && viewerConfig?.url && (
            <Button variant="outline" size="sm" className="h-8 text-[10px] font-bold gap-2" asChild>
              <a href={viewerConfig.url} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3 w-3" /> Fullscreen
              </a>
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 bg-muted/10 relative">
        {viewerConfig?.type === 'office' && (
          <iframe
            src={viewerConfig.url}
            className="w-full h-full border-none"
            title="Office Document Viewer"
          />
        )}
        {viewerConfig?.type === 'pdf' && (
          <PdfDocumentViewer pdfUrl={viewerConfig.url} title={title} />
        )}
        {viewerConfig?.type === 'image' && (
          <div className="flex items-center justify-center h-full p-8">
            <img 
              src={viewerConfig.url} 
              alt={title} 
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: 'calc(100vh - 200px)' }}
            />
          </div>
        )}
        {viewerConfig?.type === 'text' && (
          <iframe
            src={viewerConfig.url}
            className="w-full h-full border-none"
            title="Text Document Viewer"
          />
        )}
        {!viewerConfig || viewerConfig?.type === 'unsupported' && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Document not supported</p>
              <p className="text-sm text-slate-400 mt-2">
                {documentUrl ? 'Unsupported file format' : 'No document URL provided'}
              </p>
              {/* Download button removed for unsupported documents */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PdfDocumentViewer = ({ pdfUrl, title, authToken }) => {
  const pagesContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    const renderTasks = [];
    const pdfDocument = { current: null };

    const renderPdf = async () => {
      const container = pagesContainerRef.current;

      if (!container || !pdfUrl) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      container.innerHTML = "";
      try {
        const token = authToken || getAuthToken();
        console.log("PDF URL:", pdfUrl);
        console.log("TOKEN:", token);

        const fetchHeaders = {};
        
        // Skip auth token for external URLs (like Amazon S3) to avoid CORS preflight/header rejections
        const isExternal = pdfUrl.startsWith("http") && 
          !pdfUrl.includes(window.location.hostname) && 
          !pdfUrl.includes("localhost") && 
          !pdfUrl.includes("127.0.0.1");

        if (token && !isExternal) {
          fetchHeaders.Authorization = `Bearer ${token}`;
        }

        if (cancelled) return;

        loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          maxImageSize: -1, // optional, standard options
        });
        const pdf = await loadingTask.promise;
        pdfDocument.current = pdf;

        if (cancelled) {
          await pdf.destroy();
          return;
        }

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) {
            break;
          }

          const page = await pdf.getPage(pageNum);
          const baseViewport = page.getViewport({ scale: 1 });
          const availableWidth = Math.max(container.clientWidth - 32, 320);
          const scale = Math.min(1.5, availableWidth / baseViewport.width);
          const viewport = page.getViewport({ scale });

          const pageWrapper = document.createElement("div");
          pageWrapper.className = "mb-4 flex justify-center";

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (!context) {
            throw new Error("Unable to create canvas context");
          }

          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.className = "max-w-full h-auto rounded-lg border border-border bg-white shadow-sm";

          pageWrapper.appendChild(canvas);
          container.appendChild(pageWrapper);

          const renderTask = page.render({
            canvasContext: context,
            viewport,
          });

          renderTasks.push(renderTask);
          await renderTask.promise;
          page.cleanup();
        }
      } catch (renderError) {
        if (!cancelled) {
          console.error("PDF Error:", renderError);
          setError(renderError?.message || "Failed to render PDF");
          container.innerHTML = "";
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
      renderTasks.forEach((task) => {
        if (task?.cancel) {
          task.cancel();
        }
      });
      void (async () => {
        await pdfDocument.current?.cleanup();
        await pdfDocument.current?.destroy();
        await loadingTask?.destroy?.();
      })();
    };
  }, [pdfUrl, authToken]);

  return (
    <div
      className="absolute inset-0 overflow-y-auto bg-muted/5"
      onContextMenu={(event) => event.preventDefault()}
    >
      <div ref={pagesContainerRef} className="min-h-full p-4 select-none" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/20 text-center text-slate-500">
          <div>
            <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">Rendering PDF...</p>
            <p className="mt-1 text-xs text-slate-400">{title}</p>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/20 text-center">
          <div>
            <FileText className="mx-auto mb-3 h-14 w-14 text-slate-300" />
            <p className="text-sm font-semibold text-slate-500">PDF not available</p>
            <p className="mt-1 text-xs text-slate-400">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Content type selector component
const ContentViewer = ({ content, contentType, title }) => {
  if (!content) {
    return (
      <div className="bg-card flex flex-col h-[calc(100vh-140px)] border rounded-lg overflow-hidden shadow-sm">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Select content to view</p>
            <p className="text-sm text-slate-400 mt-2">Choose a content item from the sidebar</p>
          </div>
        </div>
      </div>
    );
  }

  const contentTitle = title || content.title || content.file_name || 'Content';
  const contentUrl = content.file_path || content.url;

  // Validate URL before passing to components
  const isValidContentUrl = contentUrl && typeof contentUrl === 'string' && contentUrl.trim() !== '';

  // Construct proper URL for static files
  const getFullContentUrl = (url) => {
    if (!url) return null;
    
    // If URL is already absolute, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // For relative URLs, construct full backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    
    // If URL starts with /static, it's already correct
    if (url.startsWith('/static')) {
      return `${backendUrl}${url}`;
    }
    
    // If URL starts with static/, add leading slash
    if (url.startsWith('static/')) {
      return `${backendUrl}/${url}`;
    }
    
    // Otherwise, assume it's a static file path
    return `${backendUrl}/static/${url}`;
  };

  const fullContentUrl = getFullContentUrl(contentUrl);

  if (!isValidContentUrl || !fullContentUrl) {
    return (
      <div className="bg-card flex flex-col h-[calc(100vh-140px)] border rounded-lg overflow-hidden shadow-sm">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Content not available</p>
            <p className="text-sm text-slate-400 mt-2">Invalid or missing content URL</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if the URL is a YouTube video, regardless of content_type
  const isYouTubeVideo = fullContentUrl.includes('youtube.com') || fullContentUrl.includes('youtu.be');
  
  // Check if the URL is a Vimeo video
  const isVimeoVideo = fullContentUrl.includes('vimeo.com');

  // Route to appropriate viewer based on content type and URL detection
  if (isYouTubeVideo || isVimeoVideo) {
    return <VideoPlayer videoUrl={fullContentUrl} title={contentTitle} />;
  }

  switch (contentType) {
    case 'video':
      return <VideoPlayer videoUrl={fullContentUrl} title={contentTitle} />;
    case 'urls':
      return <URLViewer url={fullContentUrl} title={contentTitle} />;
    case 'scorm':
      return <SCORMViewer scormUrl={fullContentUrl} title={contentTitle} />;
    case 'audio':
      return <AudioPlayer audioUrl={fullContentUrl} title={contentTitle} />;
    case 'file':
    default:
      return <DocumentReader title={contentTitle} documentUrl={fullContentUrl} />;
  }
};

// Content metadata component
const ContentMetadata = ({ content, contentType }) => {
  if (!content) {
    return (
      <div className="mt-10 border-t border-slate-100 pt-10 grid lg:grid-cols-3 gap-10 mb-20">
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-2xl font-bold tracking-tight text-slate-800">No Content Selected</h2>
          <p className="text-base text-slate-500 leading-relaxed max-w-2xl font-medium">
            Select a content item from the sidebar to view its details and resources.
          </p>
        </div>
      </div>
    );
  }

  const getTitle = () => content.title || content.file_name || 'Content';
  
  const getDescription = () => {
    // Always use actual content description if available
    if (content.description) {
      return content.description;
    }
    
    // Only use fallback descriptions if no content description exists
    switch (contentType) {
      case 'video':
        return 'This video content provides detailed visual information on the topic.';
      case 'urls':
        return 'This external link provides additional resources and references for learning.';
      case 'scorm':
        return 'This SCORM package contains interactive learning content with tracking capabilities.';
      case 'audio':
        return 'This audio content provides detailed information through audio format.';
      case 'file':
      default:
        return 'This document provides comprehensive written information on the topic.';
    }
  };

  const getBadges = () => {
    switch (contentType) {
      case 'video':
        return ['Video Content', 'Learning Material', 'Tutorial'].map(tag => (
          <Badge key={tag} variant="secondary" className="bg-green-50 text-green-600 border-none font-bold text-[10px] px-3 py-1 uppercase tracking-widest">{tag}</Badge>
        ));
      case 'urls':
        return ['External Link', 'Web Resource', 'Reference'].map(tag => (
          <Badge key={tag} variant="secondary" className="bg-blue-50 text-blue-600 border-none font-bold text-[10px] px-3 py-1 uppercase tracking-widest">{tag}</Badge>
        ));
      case 'scorm':
        return ['SCORM', 'Interactive', 'Tracked'].map(tag => (
          <Badge key={tag} variant="secondary" className="bg-orange-50 text-orange-600 border-none font-bold text-[10px] px-3 py-1 uppercase tracking-widest">{tag}</Badge>
        ));
      case 'audio':
        return ['Audio', 'Listening', 'Podcast'].map(tag => (
          <Badge key={tag} variant="secondary" className="bg-purple-50 text-purple-600 border-none font-bold text-[10px] px-3 py-1 uppercase tracking-widest">{tag}</Badge>
        ));
      case 'file':
      default:
        return ['Document', 'Reading Material', 'Reference'].map(tag => (
          <Badge key={tag} variant="secondary" className="bg-blue-50 text-blue-600 border-none font-bold text-[10px] px-3 py-1 uppercase tracking-widest">{tag}</Badge>
        ));
    }
  };

  const getResourceType = () => {
    switch (contentType) {
      case 'video':
        return 'Video File';
      case 'urls':
        return 'External URL';
      case 'scorm':
        return 'SCORM Package';
      case 'audio':
        return 'Audio File';
      case 'file':
      default:
        return 'Document';
    }
  };

  return (
    <div className="mt-10 border-t border-slate-100 pt-10 grid lg:grid-cols-3 gap-10 mb-20">
      <div className="lg:col-span-2 space-y-6">
        <h2 className="text-2xl font-bold tracking-tight text-slate-800">{getTitle()}</h2>
        <p className="text-base text-slate-500 leading-relaxed max-w-2xl font-medium">
          {getDescription()}
        </p>
        <div className="flex gap-2">
          {getBadges()}
        </div>
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
        <h3 className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">
          Content Info
        </h3>

        <div className="mt-4 text-sm text-slate-500">
          Learning resource for this topic.
        </div>
      </div>
    </div>
  );
};

export {
  VideoPlayer,
  AudioPlayer,
  URLViewer,
  SCORMViewer,
  DocumentReader,
  ContentViewer,
  ContentMetadata
};
